import React, { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSAL_KEY = 'pwa_install_dismissed';
const DISMISSAL_DAYS = 7;

// Get subdomain-specific app info
const getSubdomainAppInfo = () => {
  const config = (window as any).__SUBDOMAIN_CONFIG__ || {};
  const host = window.location.hostname;

  if (config.isWholesale || host.startsWith('wholesale.')) {
    return { name: 'Red Bridge Wholesale', color: '#8B2131', icon: '/wholesale-icon-192.png' };
  } else if (config.isBridgeClub || host.startsWith('bridgeclub.')) {
    return { name: 'Bridge Club', color: '#4A6741', icon: '/bridgeclub-icon-192.png' };
  } else if (config.isStoreClub || host.startsWith('storeclub.')) {
    return { name: 'Store Club', color: '#B8860B', icon: '/storeclub-icon-192.png' };
  }
  return { name: 'Red Bridge Farm', color: '#C41E24', icon: '/logo192.png' };
};

const InstallAppBanner: React.FC = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [appInfo] = useState(getSubdomainAppInfo);
  const [isIOS] = useState(/iPad|iPhone|iPod/.test(navigator.userAgent));

  useEffect(() => {
    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Check if user dismissed recently
    const dismissedAt = localStorage.getItem(DISMISSAL_KEY);
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const daysSinceDismissal = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissal < DISMISSAL_DAYS) {
        return; // Don't show banner if dismissed within last 7 days
      }
    }

    // For iOS, show instructions banner
    if (isIOS) {
      setShowBanner(true);
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isIOS]);

  const handleInstall = async () => {
    if (!installPrompt) return;

    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowBanner(false);
      setInstallPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSAL_KEY, new Date().toISOString());
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div style={styles.banner}>
      <div style={styles.content}>
        <div style={styles.iconWrapper}>
          <img src={appInfo.icon} alt={appInfo.name} style={styles.icon} />
        </div>
        <div style={styles.text}>
          <span style={styles.title}>Get the {appInfo.name} App</span>
          {isIOS ? (
            <span style={styles.subtitle}>Tap Share, then "Add to Home Screen"</span>
          ) : (
            <span style={styles.subtitle}>Quick access from your home screen</span>
          )}
        </div>
      </div>
      <div style={styles.buttons}>
        <button style={styles.dismissButton} onClick={handleDismiss}>
          Not Now
        </button>
        {!isIOS && (
          <button
            style={{ ...styles.installButton, backgroundColor: appInfo.color }}
            onClick={handleInstall}
          >
            Install
          </button>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  banner: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e0e0e0',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
    zIndex: 9999,
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    overflow: 'hidden',
    flexShrink: 0,
  },
  icon: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  title: {
    fontWeight: 600,
    fontSize: '15px',
    color: '#333',
  },
  subtitle: {
    fontSize: '13px',
    color: '#666',
  },
  buttons: {
    display: 'flex',
    gap: '8px',
    flexShrink: 0,
  },
  dismissButton: {
    padding: '8px 12px',
    backgroundColor: 'transparent',
    color: '#666',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  installButton: {
    padding: '8px 16px',
    backgroundColor: '#C41E24',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default InstallAppBanner;
